import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useStoreCheckoutUiVariationOptional } from '../context/StoreCheckoutUiVariationContext';
import StoreCheckoutBackIcon from './StoreCheckoutBackIcon';
import {
  buildStoreCartOpenUrl,
  isStoreCartDrawerViewport,
} from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import ViewportContext from '@/common/context/ViewPort/ViewPortContext';
import { useAppDispatch } from '@/store/hooks';
import { setCartOpened } from '@/store/reducers/cart';
import { setStoreCartOpened } from '@/store/reducers/storeCart';

import './StoreCheckoutHeader.scss';

const STORE_CHECKOUT_FIXED_HEADER_HEIGHT_VAR = '--store-checkout-fixed-header-height';
const MOBILE_TABLET_HEADER_MAX_WIDTH = 1023.99;

interface IStoreCheckoutHeaderProps {
  onEditStoreBackClick?: () => void;
  onNewStoreBackClick?: () => void;
}

const StoreCheckoutHeader: React.FC<IStoreCheckoutHeaderProps> = ({
  onEditStoreBackClick,
  onNewStoreBackClick,
}) => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId?: string }>();
  const dispatch = useAppDispatch();
  const { width: viewportWidth } = useContext(ViewportContext);
  const headerRef = useRef<HTMLDivElement>(null);
  const uiVariationCtx = useStoreCheckoutUiVariationOptional();
  const isOnEditPageUrl = window.location.pathname.includes('/store/edit/');

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) {
      return undefined;
    }

    const syncHeaderOffset = (): void => {
      if (window.innerWidth > MOBILE_TABLET_HEADER_MAX_WIDTH) {
        document.documentElement.style.removeProperty(STORE_CHECKOUT_FIXED_HEADER_HEIGHT_VAR);
        return;
      }

      document.documentElement.style.setProperty(
        STORE_CHECKOUT_FIXED_HEADER_HEIGHT_VAR,
        `${headerElement.offsetHeight}px`
      );
    };

    syncHeaderOffset();

    const resizeObserver = new ResizeObserver(syncHeaderOffset);
    resizeObserver.observe(headerElement);
    window.addEventListener('resize', syncHeaderOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeaderOffset);
      document.documentElement.style.removeProperty(STORE_CHECKOUT_FIXED_HEADER_HEIGHT_VAR);
    };
  }, [uiVariationCtx?.showUiVariationPicker, uiVariationCtx?.uiVariation]);

  const handleGoBack = useCallback(() => {
    if (onEditStoreBackClick) {
      onEditStoreBackClick();
      return;
    }

    if (onNewStoreBackClick) {
      onNewStoreBackClick();
      return;
    }

    const editStoreOrderId =
      orderId ?? window.location.pathname.match(/\/store\/edit\/([^/]+)/)?.[1];

    if (isOnEditPageUrl && editStoreOrderId) {
      navigate(`/order/${editStoreOrderId}`, { replace: true });
      return;
    }

    dispatch(setCartOpened(false));

    if (isStoreCartDrawerViewport(viewportWidth)) {
      dispatch(setStoreCartOpened(true));
    } else {
      dispatch(setStoreCartOpened(false));
    }

    navigate(buildStoreCartOpenUrl(viewportWidth), { replace: true });
  }, [
    dispatch,
    isOnEditPageUrl,
    navigate,
    onEditStoreBackClick,
    onNewStoreBackClick,
    orderId,
    viewportWidth,
  ]);

  return (
    <div
      ref={headerRef}
      className="store-checkout-header"
      data-testid="stores-checkout-flow-step-header"
    >
      <button
        type="button"
        className="store-checkout-header__back-btn"
        onClick={handleGoBack}
        aria-label="Go back"
      >
        <StoreCheckoutBackIcon />
      </button>
    </div>
  );
};

StoreCheckoutHeader.displayName = 'StoreCheckoutHeader';

export default StoreCheckoutHeader;
